import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Multicurrency } from '@sprinx/react-globalize';
import useMulticurrencyToPrice from '@sprinx/react-globalize/useMulticurrencyToPrice';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { currencyState, isAuthenticatedState } from '../../../api/appState';
import { ProductDetailProduct } from '../../../api/products/productDetail';
import ProductPriceElement from '../../../components/ProductPriceElement';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export interface ProductDetailPriceProps
  extends StandardDivProps<ProductDetailPriceClassKey> {
  product: ProductDetailProduct;
}

export type ProductDetailPriceClassKey = 'root';

const themeSettings = { name: 'ProductDetailPrice' };
const useStyles = makeStyles(
  () => ({
    root: {
      display: 'flex',
      alignItems: 'flex-end',
    },
    unitPrice: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      height: '100%',
    },
    totalPrice: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      marginLeft: 8,
      height: '100%',
    },
    inputRoot: {
      '& > label': {
        textAlign: 'right',
        transform: 'none',
        fontSize: 12,
      },
    },
    priceAlignRigth: {
      textAlign: 'right',
    },
    labels: {
      flex: 1,
    },
  }),
  themeSettings,
);

const ProductDetailPrice = React.forwardRef<
  HTMLDivElement,
  ProductDetailPriceProps
>(({ className, classes: pClasses, product }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const currency = useRecoilValue(currencyState);
  const getPrice = useMulticurrencyToPrice(currency);
  const t = useTranslateWithFallback();
  const isAuthenticated = useRecoilValue(isAuthenticatedState);

  const isTaxPresented = (product?.taxInfo?.value ?? 0) > 0;

  const toNumber = (val: unknown): number | undefined => {
    if (Array.isArray(val)) return getPrice(val as unknown as Multicurrency);
    if (val && typeof val === 'object') return getPrice([val] as unknown as Multicurrency);
    if (typeof val === 'number') return val;
    return undefined;
  };

  const mainPriceNum = toNumber(product?.price);
  const cu3PriceNum = toNumber(product?.extra?.cu3Price);

  const mainPriceWithTaxNum = toNumber(product?.priceWithTax);
  const cu3PriceWithTaxNum = toNumber(product?.extra?.cu3PriceWithTax);

  const showCu3Block =
    mainPriceNum !== undefined &&
    cu3PriceNum !== undefined &&
    cu3PriceNum >= mainPriceNum;

  return (
    <div ref={ref} className={clsx(classes.root, className)}>
      {isAuthenticated && (
        <>
          {showCu3Block && (
            <div className={clsx(classes.unitPrice, className)}>
              <strong className={classes.labels}>{t('common/reccomendedPrice')}</strong>

              <SecondaryPrice
                value={cu3PriceNum}
                currency={currency}
                labelStart={t('common/withoutTaxPerPc')}
              />

              {isTaxPresented && cu3PriceWithTaxNum !== undefined && (
                <SecondaryPrice
                  value={cu3PriceWithTaxNum}
                  currency={currency}
                  labelStart={t('common/withTaxPerPc')}
                />
              )}
            </div>
          )}

          <div className={clsx(classes.totalPrice, className)}>
            {showCu3Block && (
              <strong className={classes.labels}>{t('common/yourPrice')}</strong>
            )}

            <PrimaryPrice
              value={mainPriceNum}
              currency={currency}
              labelStart={t('common/withoutTax')}
            />

            {isTaxPresented && mainPriceWithTaxNum !== undefined && (
              <SecondaryPrice
                value={mainPriceWithTaxNum}
                currency={currency}
                labelStart={t('common/withTax')}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
});

export default withThemeProps(themeSettings)(ProductDetailPrice);

const PrimaryPrice = withStyles((theme) => ({
  root: {
    ...theme.typography.h6,
    color: theme.palette.text.primary,
  },
  label: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },
}))(ProductPriceElement) as typeof ProductPriceElement;

const SecondaryPrice = withStyles((theme) => ({
  root: {
    ...theme.typography.body1,
    color: theme.palette.text.primary,
  },
  label: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
  },
}))(ProductPriceElement) as typeof ProductPriceElement;
